// Frontend mirror of admin_backend/Controller/Booking/BookingValidation.js (RULES).
// Forms check the same limits the API enforces, so a mistake is shown next
// to the field instead of coming back as a toast after the round trip.
// Change both files together.

export const RULES = {
    COUPON_RE: /^[A-Za-z0-9_-]{3,30}$/,
    MAX_DISTANCE_KM: 500,
    ADDRESS_MIN: 5,
    ADDRESS_MAX: 300,
    ODOMETER_MAX: 9999999,
    FUEL_TYPE_MAX: 30,
    FUEL_ROWS_MAX: 5,
    TRIP_IMAGES_MIN: 6,
    TRIP_IMAGES_MAX: 10,
    IMAGE_MAX_BYTES: 10 * 1024 * 1024,
    DAMAGE_ROWS_MAX: 30,
    MONEY_MAX: 1000000,
    EXCESS_KM_MAX: 100000,
    EXCESS_HOURS_MAX: 2000,
    FASTAG_MAX: 100000,
    FINE_ROWS_MAX: 20,
    FINE_NAME_MIN: 2,
    FINE_NAME_MAX: 80,
    EXTEND_HOURS_MAX: 48,
    REASON_MAX: 500,
    SEARCH_MAX: 100,
    PLAN_NAME_MIN: 2,
    PLAN_NAME_MAX: 40,
    PLAN_NAME_RE: /^[A-Za-z0-9 &()+-]+$/,
    PLAN_DESC_TEXT_MIN: 5,
    PLAN_DESC_TEXT_MAX: 2000,
    CAL_NAME_MIN: 2,
    CAL_NAME_MAX: 60,
    CAL_DESC_MIN: 2,
    CAL_DESC_MAX: 300,
    CAL_PCT_MIN: -50,
    CAL_PCT_MAX: 300,
};

const blank = (v) => v === "" || v === null || v === undefined || (typeof v === "string" && !v.trim());

const decimals = (v) => {
    const s = String(v);
    const i = s.indexOf(".");
    return i === -1 ? 0 : s.length - i - 1;
};

// Returns a message for a bad number, or null when it's fine.
export const numberError = (value, { label, required = false, min = 0, max, gt, int = false, maxDecimals }) => {
    if (blank(value)) return required ? `${label} is required` : null;
    const n = Number(value);
    if (!Number.isFinite(n)) return `${label} must be a number`;
    if (int && !Number.isInteger(n)) return `${label} must be a whole number`;
    if (gt !== undefined && !(n > gt)) return `${label} must be more than ${gt}`;
    if (gt === undefined && n < min) return `${label} must be ${min} or more`;
    if (max !== undefined && n > max) return `${label} can be at most ${max.toLocaleString("en-IN")}`;
    if (maxDecimals !== undefined && decimals(value) > maxDecimals) return `${label} can have at most ${maxDecimals} decimal place${maxDecimals === 1 ? "" : "s"}`;
    return null;
};

export const textError = (value, { label, required = false, min = 0, max, pattern, patternMessage }) => {
    const text = String(value ?? "").trim();
    if (!text) return required ? `${label} is required` : null;
    if (text.length < min) return `${label} must be at least ${min} characters`;
    if (max !== undefined && text.length > max) return `${label} can be at most ${max} characters`;
    if (pattern && !pattern.test(text)) return patternMessage || `${label} is not valid`;
    return null;
};

export const htmlTextLength = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim().length;

// Fuel readings at handover. Every row needs a whole-number level 0-100.
export const fuelRowsErrors = (rows) => {
    const errors = {};
    (rows || []).forEach((row, i) => {
        const msg = numberError(row.percentage, { label: `${row.fuelType || "Fuel"} level`, required: true, min: 0, max: 100, int: true });
        if (msg) errors[i] = msg;
    });
    return errors;
};

export const odometerError = (value, { label, floor, floorLabel }) => {
    const msg = numberError(value, { label, required: true, min: 0, max: RULES.ODOMETER_MAX, int: true });
    if (msg) return msg;
    if (floor !== undefined && floor !== null && Number(value) < Number(floor)) return `${label} cannot be less than ${floorLabel} (${Number(floor).toLocaleString("en-IN")})`;
    return null;
};

export const firstError = (errors) => {
    const values = Object.values(errors || {}).flatMap((v) => (v && typeof v === "object" ? Object.values(v) : [v]));
    return values.find(Boolean) || null;
};

// Small helpers so a field shows its own message.
export const invalidClass = (msg) => (msg ? " is-invalid" : "");

// Keystroke guard for number fields. Returns { value, reason }: value is what
// the field should hold, reason says why a keystroke was refused (null when it
// was accepted). Anything that would make the field invalid (letters, a dot in
// a whole-number field, too many decimals, a minus sign where negatives aren't
// allowed, or a value over the max) is refused and the field keeps its
// previous value, so typing "1000" into a 0-100 field stops at "100". A
// minimum above 0 (e.g. an odometer floor) can't be enforced per keystroke,
// since "1" is on the way to "18420"; that stays a field error on submit.
export const checkNumberInput = (raw, prev, { min = 0, max, decimals = 0 } = {}) => {
    const text = String(raw ?? "").replace(/,/g, "").trim();
    if (text === "") return { value: "", reason: null };
    const allowNegative = min < 0;
    if (allowNegative && text === "-") return { value: text, reason: null };
    const refuse = (reason) => ({ value: prev, reason });
    if (!allowNegative && text.includes("-")) return refuse(`Minimum is ${min}`);
    if (decimals === 0 && text.includes(".")) return refuse("Whole numbers only");
    const pattern = decimals > 0
        ? new RegExp(`^${allowNegative ? "-?" : ""}\\d*(\\.\\d*)?$`)
        : new RegExp(`^${allowNegative ? "-?" : ""}\\d*$`);
    if (!pattern.test(text)) return refuse("Numbers only");
    const dot = text.indexOf(".");
    if (dot !== -1 && text.length - dot - 1 > decimals) return refuse(`Up to ${decimals} decimal place${decimals === 1 ? "" : "s"}`);
    if (text === "." || text === "-.") return { value: text.replace(".", "0."), reason: null };
    const n = Number(text);
    if (!Number.isFinite(n)) return refuse("Numbers only");
    if (max !== undefined && n > max) return refuse(`Maximum is ${Number(max).toLocaleString("en-IN")}`);
    if (min < 0 && n < min) return refuse(`Minimum is ${min}`);
    // Drop leading zeros ("007" -> "7") but keep "0" and "0.x".
    return { value: text.replace(/^(-?)0+(?=\d)/, "$1"), reason: null };
};
export const cleanNumberInput = (raw, prev, opts) => checkNumberInput(raw, prev, opts).value;
